import { TAGS } from "../constants";
import {
  CreateMedication,
  CreateProductMedication,
  DosageInstructions,
  GetMedicationsArgs,
  MedicalSupply,
  Medication,
  MedicationWithProduct,
  UpdateMedication,
} from "../types";
import { api } from "./api";

export const medicationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSupplies: builder.query<
      MedicalSupply[],
      { pharmacy_id?: number | null; active?: boolean }
    >({
      query: (params) => ({
        url: `/api/supplies`,
        method: "GET",
        params,
      }),
    }),
    getMedications: builder.query<
      { data: Medication[]; count: number },
      GetMedicationsArgs | undefined
    >({
      query: (params) => ({
        url: "/api/medications",
        method: "GET",
        params,
      }),
      providesTags: [TAGS.MEDICATIONS],
    }),
    updateMedication: builder.mutation<
      Medication,
      { id: number; body: UpdateMedication }
    >({
      query: ({ id, body }) => ({
        url: `/api/medications/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        TAGS.MEDICATION,
        TAGS.PRODUCT_MEDICATIONS,
        TAGS.MEDICATIONS,
      ],
    }),
    getInstructionsByMedicationId: builder.query<DosageInstructions[], number>({
      query: (id) => ({
        method: "GET",
        url: `/api/medications/${id}/instructions`,
      }),
    }),
    getMedicationsPerProduct: builder.query<
      MedicationWithProduct[],
      {
        product_id: number | string;
        active?: boolean;
        state?: string;
        form?: string;
        pharmacy_status?: boolean;
      }
    >({
      query: ({ product_id, ...params }) => ({
        url: `/api/medications/product/${product_id}`,
        method: "GET",
        params,
      }),
      providesTags(_result, _error, arg) {
        return [{ type: TAGS.PRODUCT_MEDICATIONS, id: arg.product_id }];
      },
    }),
    createMedication: builder.mutation<Medication, CreateMedication>({
      query: (body) => ({
        method: "POST",
        url: `/api/medications`,
        body,
      }),
      invalidatesTags: [TAGS.MEDICATIONS],
    }),
    getMedication: builder.query<Medication, number>({
      query: (id) => ({
        method: "GET",
        url: `/api/medications/${id}`,
      }),
      providesTags(_result, _error, arg) {
        return [{ type: TAGS.MEDICATION, id: arg }];
      },
    }),
    createProductMedication: builder.mutation<
      Medication,
      CreateProductMedication
    >({
      query: (body) => ({
        method: "POST",
        url: `/api/medications/product/${body.product}`,
        body: {
          medication_id: body.medication_id,
        },
      }),
      invalidatesTags: [TAGS.MEDICATIONS, TAGS.PRODUCT_MEDICATIONS],
    }),
  }),
});

export const {
  useGetInstructionsByMedicationIdQuery,
  useGetMedicationsQuery,
  useUpdateMedicationMutation,
  useGetMedicationsPerProductQuery,
  useGetSuppliesQuery,
  useCreateMedicationMutation,
  useGetMedicationQuery,
  useCreateProductMedicationMutation,
} = medicationsApi;
